import { ref } from 'vue'
import type {
  GETContainerAvailability,
  ImportSlotsUpdatedEvent,
  TerminalName,
} from '~/services/apiClient'
import { EventName } from '~/services/apiClient'
import type { SoketiEvent } from '~/compositions/useSoketi'
import { useSoketi } from '~/compositions/useSoketi'
import { IMPORT_SLOTS_WEBSOCKET_PREFIX } from '~/constants'

export function useImportSlotsWebsocket(
  onUpdate: (availability: GETContainerAvailability) => void
) {
  const websocketKey = ref<string | null>(null)
  const soketi = useSoketi()

  function subscribeToWebsocket(
    terminal: TerminalName,
    terminalBlockAvailabilityKey: string
  ) {
    const newSubscriptionKey = `${terminal}-${terminalBlockAvailabilityKey}`

    // Only subscribe if we're not already subscribed to this exact channel
    if (websocketKey.value === newSubscriptionKey) {
      return
    } else if (websocketKey.value) {
      // If we are subscribed to a different channel, unsubscribe first
      unsubscribeFromWebsocket()
    }
    websocketKey.value = newSubscriptionKey
    const channelName = IMPORT_SLOTS_WEBSOCKET_PREFIX + newSubscriptionKey
    soketi.bind(
      channelName,
      EventName.ImportSlotsUpdated,
      handleImportSlotsUpdate
    )
  }

  function unsubscribeFromWebsocket() {
    if (websocketKey.value) {
      const channelName = IMPORT_SLOTS_WEBSOCKET_PREFIX + websocketKey.value
      soketi.unbind(
        channelName,
        EventName.ImportSlotsUpdated,
        handleImportSlotsUpdate
      )
      websocketKey.value = null
    }
  }

  function handleImportSlotsUpdate(event: SoketiEvent) {
    if (event.event_name === EventName.ImportSlotsUpdated) {
      const importSlotsUpdatedEvent = event as ImportSlotsUpdatedEvent
      onUpdate(importSlotsUpdatedEvent.reading)
    } else {
      console.warn(
        `[useImportSlotsWebsocket] Received unexpected Soketi event type: ${event.event_name}`
      )
    }
  }

  return {
    subscribeToWebsocket,
    unsubscribeFromWebsocket,
    websocketKey: readonly(websocketKey),
  }
}
